import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { TriangleAlert } from "lucide-react";
import { SwapOrder } from "../stores/SwapOrder";
import { publicOrderUrl } from "../helpers/constants";
import { Alert, Button, CopyButton } from "../../ui";

const storageKey = (token: string) => `ls-swap-token-callout-minimized:${token}`;

const readMinimized = (token: string): boolean => {
  try {
    return window.localStorage.getItem(storageKey(token)) === "1";
  } catch {
    return false;
  }
};

const writeMinimized = (token: string, minimized: boolean) => {
  try {
    if (minimized) {
      window.localStorage.setItem(storageKey(token), "1");
    } else {
      window.localStorage.removeItem(storageKey(token));
    }
  } catch {
    // private mode / quota — state still works in-memory
  }
};

/**
 * The swap token is the ONLY credential on a guest's swap — no account, no
 * email, no recovery. This has to read as a warning, not a footnote, and it
 * stays visible for the whole life of the swap rather than only at creation.
 * Users can minimize it (X) after saving; a compact bar lets them expand again.
 */
const SwapTokenCallout: React.FC<{ order: SwapOrder }> = observer(({ order }) => {
  const token = order.publicToken;
  const url = publicOrderUrl(token);
  const [minimized, setMinimized] = useState(() => readMinimized(token));

  useEffect(() => {
    setMinimized(readMinimized(token));
  }, [token]);

  const minimize = () => {
    setMinimized(true);
    writeMinimized(token, true);
  };

  const expand = () => {
    setMinimized(false);
    writeMinimized(token, false);
  };

  if (minimized) {
    return (
      <div
        className="alert-warn flex flex-wrap items-center justify-between gap-3"
        role="status"
      >
        <div className="flex min-w-0 items-center gap-2">
          <TriangleAlert size={16} className="shrink-0" />
          <span className="text-sm font-semibold">
            Swap token hidden — expand if you still need to copy it
          </span>
        </div>
        <Button variant="warn" size="xs" onClick={expand}>
          Show token
        </Button>
      </div>
    );
  }

  return (
    <Alert
      tone="warn"
      title="Save your swap token now"
      icon={<TriangleAlert size={20} />}
      onClose={minimize}
      closeLabel="Minimize token reminder"
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm font-semibold">
          This token is the only way to reach this swap again. We cannot recover
          it for you — if you lose it, you lose access to the swap, including the
          refund form if something goes wrong.
        </p>

        <div>
          <div className="mb-1 text-xs opacity-80">Swap token</div>
          <div className="flex flex-wrap items-center gap-2">
            <code className="min-w-0 flex-1 select-all overflow-x-auto rounded-md border border-warn/20 bg-paper-elevated px-2.5 py-2 text-sm text-ink">
              {token}
            </code>
            <CopyButton value={token} label="Copy token" variant="warn" />
          </div>
        </div>

        <div className="border-t border-dashed border-warn/30" />

        <div>
          <div className="mb-1 text-xs opacity-80">Or bookmark the direct link</div>
          <div className="flex flex-wrap items-center gap-2">
            <code className="min-w-0 flex-1 select-all overflow-x-auto rounded-md border border-warn/20 bg-paper-elevated px-2.5 py-2 text-xs text-ink">
              {url}
            </code>
            <CopyButton value={url} label="Copy link" variant="secondary" />
          </div>
        </div>
      </div>
    </Alert>
  );
});

export default SwapTokenCallout;
